/**
 * Point-in-polygon geo-join service (M9 shared-engine slice).
 *
 * Assigns each geocoded dataset point to its containing boundary at a chosen
 * tier and rolls up per-boundary aggregates of the user's OWN columns, so the
 * user's data can be shaded alongside census data. Reuses the M8 spatial core
 * (`@/lib/analysis/geometry.pointInRing`) for the predicate — the join is spatial,
 * NOT a per-call vendor lookup (M9.md: no paid geocoding to attach census data).
 *
 * Accuracy risk (M9.md): interpolated / ZIP-centroid points may fall in the wrong
 * boundary. Mitigation lives here: points below a `confidenceFloor` are held OUT
 * of the join and reported as low-confidence, per-boundary `meanConfidence` is
 * surfaced (the `geocode_accuracy` signal), and matched + every unmatched bucket
 * reconcile EXACTLY to the input row count.
 *
 * Pure: takes points + boundaries as input, no DB, no maplibre — fully testable.
 */

import { pointInRing } from "@/lib/analysis/geometry";
import type { Point } from "@/lib/map/pointset";
import type { Boundary, Tier } from "./types";

/** Default geocode-confidence floor — points below this are held out of the join. */
export const DEFAULT_CONFIDENCE_FLOOR = 0.5;

/** Numeric aggregate of one user column over a boundary's member points. */
export interface ColumnAggregate {
  sum: number;
  mean: number;
  min: number;
  max: number;
  /** Rows with a finite numeric value for this column (≤ member count). */
  count: number;
}

/** Per-boundary rollup of the user's own rows (M9 rollup popover reads this). */
export interface BoundaryAggregate {
  boundaryId: string;
  name?: string;
  /** Member rows assigned to this boundary. */
  count: number;
  memberRowIds: string[];
  /** Numeric aggregates per requested column. */
  columns: Record<string, ColumnAggregate>;
  /** Mean geocode confidence of the member rows — the surfaced accuracy signal. */
  meanConfidence: number;
}

export interface GeoJoinResult {
  tier: Tier;
  /** Total input rows — matched + every unmatched bucket sums to this. */
  rowCount: number;
  /** Rows assigned to a boundary (above the confidence floor). */
  matched: number;
  /** Rows that could not be assigned (sum of the two buckets below). */
  unmatched: number;
  /** Above-floor rows that fell inside NO boundary. */
  unmatchedNoBoundary: string[];
  /** Rows held out because their geocode confidence was below the floor. */
  unmatchedLowConfidence: string[];
  boundaries: BoundaryAggregate[];
  /** Confidence floor applied. */
  confidenceFloor: number;
}

export interface GeoJoinOptions {
  /** User columns to aggregate (numeric). Defaults to none (counts only). */
  columns?: string[];
  /** Geocode-confidence floor; rows below it are held out. */
  confidenceFloor?: number;
}

function emptyAggregate(): ColumnAggregate {
  return { sum: 0, mean: 0, min: Infinity, max: -Infinity, count: 0 };
}

/** True when the point is inside ANY of the boundary's outer rings. */
function inBoundary(point: Point, boundary: Boundary): boolean {
  for (const ring of boundary.geometry) {
    if (pointInRing(point.lng, point.lat, ring)) return true;
  }
  return false;
}

/**
 * Geo-join `points` to `boundaries`, rolling up per-boundary aggregates of the
 * requested user columns. Each point is assigned to the FIRST boundary that
 * contains it (fixture boundaries are non-overlapping, so "first" == "the one").
 * Matched + unmatchedNoBoundary + unmatchedLowConfidence reconcile to rowCount.
 */
export function geoJoin(
  points: Point[],
  boundaries: Boundary[],
  tier: Tier,
  opts: GeoJoinOptions = {},
): GeoJoinResult {
  const columns = opts.columns ?? [];
  const floor = opts.confidenceFloor ?? DEFAULT_CONFIDENCE_FLOOR;

  const tierBoundaries = boundaries.filter((b) => b.tier === tier);

  interface Acc {
    boundary: Boundary;
    memberRowIds: string[];
    confidenceSum: number;
    columns: Record<string, ColumnAggregate>;
  }
  const accById = new Map<string, Acc>();
  for (const b of tierBoundaries) {
    const cols: Record<string, ColumnAggregate> = {};
    for (const c of columns) cols[c] = emptyAggregate();
    accById.set(b.id, { boundary: b, memberRowIds: [], confidenceSum: 0, columns: cols });
  }

  const unmatchedNoBoundary: string[] = [];
  const unmatchedLowConfidence: string[] = [];
  let matched = 0;

  for (const p of points) {
    if (p.confidence < floor) {
      unmatchedLowConfidence.push(p.id);
      continue;
    }
    const boundary = tierBoundaries.find((b) => inBoundary(p, b));
    if (!boundary) {
      unmatchedNoBoundary.push(p.id);
      continue;
    }
    const acc = accById.get(boundary.id)!;
    acc.memberRowIds.push(p.id);
    acc.confidenceSum += p.confidence;
    for (const c of columns) {
      const raw = Number(p.props[c]);
      if (!Number.isFinite(raw)) continue;
      const agg = acc.columns[c]!;
      agg.sum += raw;
      agg.count += 1;
      if (raw < agg.min) agg.min = raw;
      if (raw > agg.max) agg.max = raw;
    }
    matched += 1;
  }

  const boundaryAggregates: BoundaryAggregate[] = [];
  for (const acc of accById.values()) {
    const count = acc.memberRowIds.length;
    if (count === 0) continue; // omit empty boundaries from the rollup
    const cols: Record<string, ColumnAggregate> = {};
    for (const c of columns) {
      const a = acc.columns[c]!;
      cols[c] = {
        sum: a.sum,
        mean: a.count > 0 ? a.sum / a.count : 0,
        min: a.count > 0 ? a.min : 0,
        max: a.count > 0 ? a.max : 0,
        count: a.count,
      };
    }
    boundaryAggregates.push({
      boundaryId: acc.boundary.id,
      ...(acc.boundary.name !== undefined ? { name: acc.boundary.name } : {}),
      count,
      memberRowIds: acc.memberRowIds,
      columns: cols,
      meanConfidence: acc.confidenceSum / count,
    });
  }

  const unmatched = unmatchedNoBoundary.length + unmatchedLowConfidence.length;
  return {
    tier,
    rowCount: points.length,
    matched,
    unmatched,
    unmatchedNoBoundary,
    unmatchedLowConfidence,
    boundaries: boundaryAggregates,
    confidenceFloor: floor,
  };
}
